import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";

import {
  useLatestAbuseAt,
  useLatestProfitAlertAt,
} from "../context/SubscriptionContext";
import { GET_ABUSE_HISTORY } from "../components/graphql/abuse";
import { GET_PROFIT_ALERT_LIST_TODAY } from "../components/graphql/profitAlert";
import {
  getLatestAbuseHistoryAt,
  getLatestProfitAlertAt,
} from "./newIndicatorUtils.mjs";
import {
  emitLastSeenSync,
  subscribeLastSeenSync,
} from "./newIndicatorSync.mjs";

export type NewIndicatorCategory = "abuse" | "profitAlert";

export interface UseNewIndicatorResult {
  hasNew: boolean;
  markSeen: () => void;
}

const lsKey = (category: NewIndicatorCategory) => `lastSeenAt_${category}`;

function readLastSeen(category: NewIndicatorCategory): number {
  try {
    const raw = localStorage.getItem(lsKey(category));
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastSeen(category: NewIndicatorCategory, value: number): void {
  try {
    localStorage.setItem(lsKey(category), String(value));
  } catch {
    /* 시크릿 모드/차단 환경 — 무시 */
  }
}

/**
 * 카테고리별 "새 이벤트 있음" 상태와 "확인 처리(markSeen)" 를 반환한다.
 *
 * - 최초 방문(localStorage 미설정) → lastSeenAt = 0 → 기존 이력이 있으면 hasNew = true
 * - markSeen 호출 시 Date.now() 를 localStorage 에 저장
 * - latestAt = max(subscription 수신 시각, 초기 쿼리의 최대 시각)
 */
export function useNewIndicator(
  category: NewIndicatorCategory,
): UseNewIndicatorResult {
  const [lastSeenAt, setLastSeenAt] = useState<number>(() =>
    readLastSeen(category),
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const unsubscribeSync = subscribeLastSeenSync(
      window,
      category,
      (value: number) => {
        setLastSeenAt(value);
      },
    );

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== lsKey(category)) return;
      setLastSeenAt(readLastSeen(category));
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      unsubscribeSync();
      window.removeEventListener("storage", handleStorage);
    };
  }, [category]);

  // Context의 subscription 최신 수신 시각 (두 훅을 모두 호출하고 category로 선택)
  const latestAbuseAt = useLatestAbuseAt();
  const latestProfitAlertAt = useLatestProfitAlertAt();
  const latestFromContext =
    category === "abuse" ? latestAbuseAt : latestProfitAlertAt;

  // 초기 최신 시각 확보용 Query (cache-first로 중복 호출 방지)
  const abuseQuery = useQuery(GET_ABUSE_HISTORY, {
    variables: { limit: 50 },
    fetchPolicy: "cache-first",
    skip: category !== "abuse",
  });
  const profitQuery = useQuery(GET_PROFIT_ALERT_LIST_TODAY, {
    fetchPolicy: "cache-first",
    skip: category !== "profitAlert",
  });

  const latestFromQuery = useMemo<number>(() => {
    if (category === "abuse") {
      return getLatestAbuseHistoryAt(abuseQuery.data?.getAbuseHistory ?? []);
    }
    return getLatestProfitAlertAt(profitQuery.data?.profitAlertListToday ?? []);
  }, [category, abuseQuery.data, profitQuery.data]);

  const latestAt = useMemo<number>(() => {
    const ctx = latestFromContext ?? 0;
    return Math.max(ctx, latestFromQuery);
  }, [latestFromContext, latestFromQuery]);

  const hasNew = latestAt > 0 && latestAt > lastSeenAt;

  const markSeen = useCallback(() => {
    const now = Date.now();
    writeLastSeen(category, now);
    if (typeof window !== "undefined") {
      emitLastSeenSync(window, category, now);
    }
    setLastSeenAt(now);
  }, [category]);

  return { hasNew, markSeen };
}
